import { Injectable } from '@angular/core';
import { AbsentismoDiario } from 'app/modules/common/entities/absentismodiario.model';
import { AbsentismoMensual } from 'app/modules/common/entities/absentismomensual.model';
import { Moment } from 'moment';
import { CompanyDashboardService } from './company.dashboard.service';



/**
 * Columns of a four column table (Mensual and Annuel)
 */
interface FourColumnTable {
    label: string;
    cyear: number | string;
    pyear: number | string;
    var: string;
}

/**
 * Columns of the Diario table
 */
interface TwoColumnTable {
    label: string;
    fiels: number | string;
}

/**
 * Data sources for Dashboard Tables
 */
interface DashboardTableData {
    annualTable: FourColumnTable[];
    mensualTable1: FourColumnTable[];
    mensualTable2: FourColumnTable[];
    diarioTable: TwoColumnTable[];
}



@Injectable({
    providedIn: 'root'
})
export class DashboardTablesService {


    /**
     * Column names for tables
     */
    public get columnNames() {
        return this._columnNames;
    }
    private _columnNames = {
        twoColumnsTable: ['label', 'fiels'],
        fourColumnsTable: ['label', 'cyear', 'pyear', 'var'],
    };

    //#region TABLE DATA SOURCES

    // default data sources are used when API returns null. It is so sad that API returns NULL :(

    private defaultAnnualDataSource: FourColumnTable[] = [
        { label: 'Total bajas', cyear: 0, pyear: 0, var: '0.0%' },
        { label: 'Bajas nuevas', cyear: 0, pyear: 0, var: '0.0%' },
        { label: 'Altas', cyear: 0, pyear: 0, var: '0.0%' },
        { label: 'Plantilla', cyear: 0, pyear: 0, var: '0.0%' },
        { label: 'Duración media casos cerrados', cyear: 0, pyear: 0, var: '0.0%' },
    ];

    private defaultMensualDatasource1: FourColumnTable[] = [
        { label: 'Total bajas', cyear: 0, pyear: 0, var: '0.0%' },
        { label: 'Bajas nuevas', cyear: 0, pyear: 0, var: '0.0%' },
        { label: 'Altas', cyear: 0, pyear: 0, var: '0.0%' },
        { label: 'Bajas diarias', cyear: 0, pyear: 0, var: '0.0%' },
        { label: 'Jornadas perdidas', cyear: 0, pyear: 0, var: '0.0%' },
        { label: 'Duración media casos cerrados', cyear: 0, pyear: 0, var: '0.0%' },
    ];

    private defaultMensualDatasource2: FourColumnTable[] = [
        { label: 'Plantilla', cyear: 0, pyear: 0, var: '0.0%' },
        { label: 'Indice de incidencia', cyear: 0, pyear: 0, var: '0.0%' },
        { label: 'Indice de prevalencia', cyear: 0, pyear: 0, var: '0.0%' },
        { label: 'Absentismo mensual', cyear: 0, pyear: 0, var: '0.0%' },
        { label: 'Absentismo acumulado', cyear: 0, pyear: 0, var: '0.0%' },
        { label: 'Absentismo internaual', cyear: 0, pyear: 0, var: '0.0%' },
    ];

    private defaultDiarioDatasource: TwoColumnTable[] = [
        { label: 'Bajas', fiels: 0 },
        { label: 'Bajas nuevas', fiels: 0 },
        { label: 'Duración media casos abiertos', fiels: 0 },
        { label: 'Absentismo diario', fiels: 0 },
    ];

    /**
     * Data sources for tables
     */
    public get tableData(): DashboardTableData {
        return this._tableDataSources;
    }
    private _tableDataSources: DashboardTableData = {
        annualTable: this.defaultAnnualDataSource,
        mensualTable1: this.defaultMensualDatasource1,
        mensualTable2: this.defaultMensualDatasource2,
        diarioTable: this.defaultDiarioDatasource,
    };

    //#endregion



    constructor(
        private dashboardService: CompanyDashboardService,
    ) { }


    /**
     * Load table data from API for the given date
     */
    public feedData = (empressaId: number, date: Moment) => {

        // load absentism mensual and annual data
        this.loadAbsentismAnnualMensualData(empressaId, date.year(), date.month());

        // load AbsentismoDiario data
        this.loadAbsentismoDiarioData(empressaId, date);

    };



    //#region SET TABLE DATA

    /**
     * Load AbsentismMensual data from API and feed into Mensual and Annual tables
     * @param empressaId Company ID
     * @param year Year of month
     * @param month Month
     */
    private loadAbsentismAnnualMensualData = (empressaId: number, year: number, month: number) => {

        // Get data
        this.dashboardService.getAbsentismMensualData(empressaId, year, month)
            .subscribe(res => {

                if (res.code == 0) {

                    // convert null integers to zero
                    this.dashboardService.convertNullNumbersToZero(res.result);

                    // set table data
                    this.setAnnualTableData(res.result);
                    this.setMensualTableData(res.result);

                }
                else {
                    console.error(res.message);
                }

            }, err => {
                console.error(err);
            });

    };


    /**
     * Load AbsentismoDiario data from API and feed into Diario table
     */
    private loadAbsentismoDiarioData = (empressaId: number, fecha: Moment) => {

        // Get data
        this.dashboardService.getAbsentismoDiariolData(empressaId, fecha)
            .subscribe(res => {

                if (res.code == 0) {

                    // convert null integers to zero
                    this.dashboardService.convertNullNumbersToZero(res.result);

                    // set table data
                    this.setDiarioTableData(res.result);

                }
                else {
                    console.error(res.message);
                }

            }, err => {
                console.error(err);
            });

    };


    /**
     * Set datasource of annual table
     * @param data AbsentismoMensual data
     */
    private setAnnualTableData = (data: AbsentismoMensual) => {

        // if data is null, set to default values
        if (data === null)
            this._tableDataSources.annualTable = this.defaultAnnualDataSource;

        else {
            this._tableDataSources.annualTable = [
                {
                    label: 'Total bajas', cyear: data.totalBajasAcum, pyear: data.totalBajasAcumAnterior, var: this.toOnePointPercentage(data.varTotalBajasAcum)
                },
                {
                    label: 'Bajas nuevas', cyear: data.bajasNuevasAcum, pyear: data.bajasNuevasAcumAnterior, var: this.toOnePointPercentage(data.varBajasNuevasAcum)
                },
                { label: 'Altas', cyear: data.altasAcum, pyear: data.altasAcumAnterior, var: this.toOnePointPercentage(data.varAltasAcum) },
                { label: 'Plantilla', cyear: data.plantillaAcum, pyear: data.plantillaAcumAnterior, var: this.toOnePointPercentage(data.varPlantillaAcum) },
                {
                    label: 'Duración media casos cerrados',
                    cyear: this.toOnePoint(data.duracionMediaAcum), pyear: this.toOnePoint(data.duracionMediaAcumAnterior),
                    var: this.toOnePointPercentage(data.varDuracionMediaAcum)
                },
            ];
        }

    };


    /**
     * Set Mensual Table data
     * @param data AbsentismoMensual data
     */
    private setMensualTableData = (data: AbsentismoMensual) => {

        // set default values if data is null
        if (data === null) {

            this._tableDataSources.mensualTable1 = this.defaultMensualDatasource1;
            this._tableDataSources.mensualTable2 = this.defaultMensualDatasource2;

        } else {

            // first part of mensual table
            this._tableDataSources.mensualTable1 = [
                {
                    label: 'Total bajas', cyear: data.totalBajas, pyear: data.totalBajasAnterior,
                    var: this.toOnePointPercentage(data.varTotalBajas)
                },
                {
                    label: 'Bajas nuevas', cyear: data.bajasNuevas, pyear: data.bajasNuevasAnterior,
                    var: this.toOnePointPercentage(data.varBajasNuevas)
                },
                {
                    label: 'Altas', cyear: data.altas, pyear: data.altasAnterior,
                    var: this.toOnePointPercentage(data.varAltas)
                },
                {
                    label: 'Bajas diarias', cyear: data.mediaBajaDiaria, pyear: data.mediaBajaDiariaAnterior,
                    var: this.toOnePointPercentage(data.varMediaDiaria)
                },
                {
                    label: 'Jornadas perdidas', cyear: data.jornadasPerdidas, pyear: data.jornadasPerdidasAnterior,
                    var: this.toOnePointPercentage(data.varJornadasPerdidas)
                },
                {
                    label: 'Duración media casos cerrados', cyear: this.toOnePoint(data.duracionMedia), pyear: this.toOnePoint(data.duracionMediaAnterior),
                    var: this.toOnePointPercentage(data.varDuracionMedia)
                },
            ];

            // second part of mensual table
            this._tableDataSources.mensualTable2 = [
                {
                    label: 'Plantilla', cyear: data.plantilla, pyear: data.plantillaAnterior,
                    var: this.toOnePointPercentage(data.varPlantilla)
                },
                {
                    label: 'Indice de incidencia', cyear: this.toOnePointPercentage(data.tasaIncidencia),
                    pyear: this.toOnePointPercentage(data.tasaIncidenciaAnterior),
                    var: this.toOnePointPercentage(data.varTasaIncidencia)
                },
                {
                    label: 'Indice de prevalencia', cyear: this.toOnePointPercentage(data.tasaPrevalencia),
                    pyear: this.toOnePointPercentage(data.tasaPrevalenciaAnterior),
                    var: this.toOnePointPercentage(data.varTasaPrevalencia)
                },
                {
                    label: 'Absentismo mensual', cyear: this.toOnePointPercentage(data.absentismo),
                    pyear: this.toOnePointPercentage(data.absAnterior),
                    var: this.toOnePointPercentage(data.varAbsentismo)
                },
                {
                    label: 'Absentismo acumulado', cyear: this.toOnePointPercentage(data.absAcumulado),
                    pyear: this.toOnePointPercentage(data.absAcumAnterior),
                    var: this.toOnePointPercentage(data.varAbsAcumulado)
                },
                {
                    label: 'Absentismo internaual', cyear: this.toOnePointPercentage(data.absInteranual),
                    pyear: this.toOnePointPercentage(data.absInterAnterior),
                    var: this.toOnePointPercentage(data.varAbsInteranual)
                },
            ];

        }

    };


    /**
     * Set Diario Table data
     * @param data AbsentismoDiario data
     */
    private setDiarioTableData = (data: AbsentismoDiario) => {

        // set default values if data is null
        if (data === null)
            this._tableDataSources.diarioTable = this.defaultDiarioDatasource;

        else {
            this._tableDataSources.diarioTable = [
                { label: 'Bajas', fiels: data.bajas },
                { label: 'Bajas nuevas', fiels: data.bajasNuevas },
                { label: 'Duración media casos abiertos', fiels: this.toOnePoint(data.duracionMedia) },
                { label: 'Absentismo diario', fiels: this.toOnePointPercentage(data.absentismo) },
            ];
        }

    };

    //#endregion



    //#region UTILS

    /**
     * Convert the given number to string format as -> 0.0%
     * @param num Number
     * @returns string in format 0.0%
     */
    private toOnePointPercentage = (num: number) => `${num.toFixed(1)}%`;


    /**
     * Convert the given number to string format as -> 0.0
     * @param num Number
     * @returns string in format 0.0
     */
    private toOnePoint = (num: number) => num.toFixed(1);

    //#endregion


}
